import React, { useState } from 'react'
import { motion } from 'framer-motion'
import { CheckSquare, ArrowRight, CheckCircle, Clock } from 'lucide-react'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { performAIAnalysis } from '../../lib/ai-analysis'
import toast from 'react-hot-toast'

interface MCQAssessmentProps {
  employeeId: string
  onComplete: (results: any) => void
}

interface MCQQuestion {
  id: string
  question: string
  options: { value: number; label: string }[]
  category: 'stress' | 'engagement' | 'satisfaction' | 'workload'
}

const mcqQuestions: MCQQuestion[] = [
  {
    id: 'stress_level',
    question: 'How would you rate your current stress level at work?',
    category: 'stress',
    options: [
      { value: 1, label: 'Very Low - I feel calm and relaxed' },
      { value: 2, label: 'Low - Manageable stress levels' },
      { value: 3, label: 'Moderate - Some stress but coping well' },
      { value: 4, label: 'High - Feeling quite stressed' },
      { value: 5, label: 'Very High - Overwhelmed and struggling' }
    ]
  },
  {
    id: 'job_satisfaction',
    question: 'How satisfied are you with your current role and responsibilities?',
    category: 'satisfaction',
    options: [
      { value: 5, label: 'Very Satisfied - Love what I do' },
      { value: 4, label: 'Satisfied - Generally happy with my role' },
      { value: 3, label: 'Neutral - It\'s okay, neither good nor bad' },
      { value: 2, label: 'Dissatisfied - Not happy with current role' },
      { value: 1, label: 'Very Dissatisfied - Really unhappy' }
    ]
  },
  {
    id: 'workload_management',
    question: 'How manageable is your current workload?',
    category: 'workload',
    options: [
      { value: 5, label: 'Very Manageable - Good balance' },
      { value: 4, label: 'Manageable - Can handle current tasks' },
      { value: 3, label: 'Moderate - Sometimes challenging' },
      { value: 2, label: 'Difficult - Often struggling to keep up' },
      { value: 1, label: 'Unmanageable - Completely overwhelmed' }
    ]
  },
  {
    id: 'team_support',
    question: 'How supported do you feel by your team and manager?',
    category: 'engagement',
    options: [
      { value: 5, label: 'Very Supported - Excellent team support' },
      { value: 4, label: 'Supported - Good team relationships' },
      { value: 3, label: 'Neutral - Average support levels' },
      { value: 2, label: 'Unsupported - Limited team support' },
      { value: 1, label: 'Very Unsupported - Feel isolated' }
    ]
  },
  {
    id: 'work_life_balance',
    question: 'How would you rate your work-life balance?',
    category: 'satisfaction',
    options: [
      { value: 5, label: 'Excellent - Perfect balance' },
      { value: 4, label: 'Good - Generally well balanced' },
      { value: 3, label: 'Fair - Could be better' },
      { value: 2, label: 'Poor - Work dominates personal time' },
      { value: 1, label: 'Very Poor - No work-life balance' }
    ]
  }
]

export const MCQAssessment: React.FC<MCQAssessmentProps> = ({
  employeeId,
  onComplete
}) => {
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0)
  const [responses, setResponses] = useState<Record<string, number>>({})
  const [isProcessing, setIsProcessing] = useState(false)
  const [assessmentComplete, setAssessmentComplete] = useState(false)
  const [results, setResults] = useState<any>(null)

  const currentQuestion = mcqQuestions[currentQuestionIndex]
  const isLastQuestion = currentQuestionIndex === mcqQuestions.length - 1

  const handleOptionSelect = (value: number) => {
    const newResponses = {
      ...responses,
      [currentQuestion.id]: value
    }
    setResponses(newResponses)

    // Auto-advance after selection
    setTimeout(() => {
      if (isLastQuestion) {
        completeAssessment(newResponses)
      } else {
        setCurrentQuestionIndex(prev => prev + 1)
      }
    }, 500)
  }

  const completeAssessment = async (allResponses: Record<string, number>) => {
    setIsProcessing(true)
    
    try {
      // Calculate metrics from MCQ responses
      const stressScore = (5 - (allResponses.stress_level || 3)) / 4 // Invert stress (lower is better)
      const satisfactionScore = (allResponses.job_satisfaction || 3) / 5
      const workloadScore = (allResponses.workload_management || 3) / 5
      const supportScore = (allResponses.team_support || 3) / 5
      const balanceScore = (allResponses.work_life_balance || 3) / 5

      const overallWellness = (satisfactionScore + workloadScore + supportScore + balanceScore + stressScore) / 5

      // Generate text summary for AI analysis
      const textSummary = generateTextSummary(allResponses)

      // Perform AI analysis on the summary
      const aiAnalysis = await performAIAnalysis({
        text: textSummary,
        employee_context: {
          id: employeeId,
          department: 'Engineering',
          role: 'Developer',
          tenure_months: 12,
          previous_assessments: 0
        },
        analysis_type: 'comprehensive'
      })

      const assessmentResults = {
        employee_id: employeeId,
        assessment_type: 'mcq',
        responses: allResponses,
        overall_metrics: {
          sentiment_score: overallWellness,
          stress_level: 1 - stressScore, // Convert back to stress level
          engagement_level: (supportScore + satisfactionScore) / 2,
          burnout_risk: (1 - stressScore + 1 - workloadScore) / 2,
          satisfaction_score: satisfactionScore,
          workload_score: workloadScore,
          support_score: supportScore,
          balance_score: balanceScore
        },
        ai_insights: aiAnalysis,
        recommendations: generateMCQRecommendations(allResponses),
        completed_at: new Date().toISOString()
      }

      setResults(assessmentResults)
      setAssessmentComplete(true)
      onComplete(assessmentResults)
      
      toast.success('Quick survey completed successfully!')
    } catch (error) {
      console.error('Error completing assessment:', error)
      toast.error('Failed to complete assessment')
    } finally {
      setIsProcessing(false)
    }
  }

  const generateTextSummary = (responses: Record<string, number>): string => {
    const summaries = []
    
    if (responses.stress_level) {
      const stressLabels = ['very low', 'low', 'moderate', 'high', 'very high']
      summaries.push(`stress level is ${stressLabels[responses.stress_level - 1]}`)
    }
    
    if (responses.job_satisfaction) {
      const satisfactionLabels = ['very dissatisfied', 'dissatisfied', 'neutral', 'satisfied', 'very satisfied']
      summaries.push(`job satisfaction is ${satisfactionLabels[responses.job_satisfaction - 1]}`)
    }
    
    if (responses.workload_management) {
      const workloadLabels = ['unmanageable', 'difficult', 'moderate', 'manageable', 'very manageable']
      summaries.push(`workload is ${workloadLabels[responses.workload_management - 1]}`)
    }

    return `Employee reports that their ${summaries.join(', ')}.`
  }

  const generateMCQRecommendations = (responses: Record<string, number>): string[] => {
    const recommendations = []

    if (responses.stress_level >= 4) {
      recommendations.push('Consider stress management techniques and speak with your manager about workload')
      recommendations.push('Take regular breaks and practice mindfulness exercises')
    }

    if (responses.job_satisfaction <= 2) {
      recommendations.push('Discuss career development opportunities with your manager')
      recommendations.push('Explore ways to make your role more engaging and fulfilling')
    }

    if (responses.workload_management <= 2) {
      recommendations.push('Prioritize tasks and consider delegating when possible')
      recommendations.push('Discuss workload distribution with your team lead')
    }

    if (responses.team_support <= 2) {
      recommendations.push('Engage more with team members and seek mentorship opportunities')
      recommendations.push('Communicate openly with your manager about support needs')
    }

    if (responses.work_life_balance <= 2) {
      recommendations.push('Set clear boundaries between work and personal time')
      recommendations.push('Discuss flexible work arrangements with your manager')
    }

    if (recommendations.length === 0) {
      recommendations.push('Continue maintaining your positive work habits')
      recommendations.push('Consider sharing your successful strategies with colleagues')
    }

    return recommendations
  }

  if (assessmentComplete && results) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-2xl mx-auto"
      >
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-emerald-600" />
          </div>
          
          <h2 className="text-2xl font-bold text-slate-900 mb-4">
            Survey Complete!
          </h2>
          
          <p className="text-slate-600 mb-6">
            Thank you for completing the wellness survey. Your responses provide valuable insights into your wellbeing.
          </p>

          {/* Metrics grid */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-sage-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-sage-700">
                {Math.round(results.overall_metrics.sentiment_score * 100)}%
              </div>
              <div className="text-sm text-sage-600">Overall Wellness</div>
            </div>
            
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-blue-700">
                {Math.round(results.overall_metrics.satisfaction_score * 100)}%
              </div>
              <div className="text-sm text-blue-600">Job Satisfaction</div>
            </div>

            <div className="bg-emerald-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-emerald-700">
                {Math.round(results.overall_metrics.support_score * 100)}%
              </div>
              <div className="text-sm text-emerald-600">Team Support</div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4">
              <div className="text-2xl font-bold text-purple-700">
                {Math.round(results.overall_metrics.balance_score * 100)}%
              </div>
              <div className="text-sm text-purple-600">Work-Life Balance</div>
            </div>
          </div>

          {/* Key recommendations */}
          {results.recommendations.length > 0 && (
            <div className="text-left mb-6">
              <h3 className="font-semibold text-slate-900 mb-3">Personalized Recommendations:</h3>
              <ul className="space-y-2">
                {results.recommendations.slice(0, 3).map((rec: string, index: number) => (
                  <li key={index} className="flex items-start">
                    <div className="w-2 h-2 bg-purple-500 rounded-full mt-2 mr-3 flex-shrink-0" />
                    <span className="text-sm text-slate-600">{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <Button
            onClick={() => window.location.reload()}
            className="bg-purple-600 hover:bg-purple-700 text-white"
          >
            Take Another Assessment
          </Button>
        </Card>
      </motion.div>
    )
  }

  if (isProcessing) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="p-8 text-center">
          <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckSquare className="w-8 h-8 text-purple-600 animate-pulse" />
          </div>
          <h3 className="text-lg font-semibold text-slate-900 mb-2">
            Processing Your Responses
          </h3>
          <p className="text-slate-600">
            Analyzing your survey responses and generating insights...
          </p>
        </Card>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-600">
            Question {currentQuestionIndex + 1} of {mcqQuestions.length}
          </span>
          <span className="text-sm text-slate-500">
            {Math.round(((currentQuestionIndex + 1) / mcqQuestions.length) * 100)}% Complete
          </span>
        </div>
        <div className="w-full bg-slate-200 rounded-full h-2">
          <motion.div
            className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full"
            initial={{ width: 0 }}
            animate={{ width: `${((currentQuestionIndex + 1) / mcqQuestions.length) * 100}%` }}
            transition={{ duration: 0.5 }}
          />
        </div>
      </div>

      {/* Current question */}
      <Card className="p-8 mb-6">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckSquare className="w-6 h-6 text-purple-600" />
          </div>
          
          <h2 className="text-xl font-semibold text-slate-900 mb-4">
            Quick Wellness Survey
          </h2>
          
          <p className="text-lg text-slate-700 leading-relaxed mb-6">
            {currentQuestion.question}
          </p>
        </div>

        {/* Options */}
        <div className="space-y-3">
          {currentQuestion.options.map((option, index) => (
            <motion.button
              key={option.value}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: index * 0.1 }}
              onClick={() => handleOptionSelect(option.value)}
              className="w-full p-4 text-left border border-slate-200 rounded-lg hover:border-purple-300 hover:bg-purple-50 transition-all duration-200 group"
            >
              <div className="flex items-center">
                <div className="w-4 h-4 border-2 border-slate-300 rounded-full mr-4 group-hover:border-purple-500 transition-colors" />
                <span className="text-slate-700 group-hover:text-purple-700 transition-colors">
                  {option.label}
                </span>
              </div>
            </motion.button>
          ))}
        </div>
      </Card>

      {/* Assessment info */}
      <div className="text-center text-sm text-slate-500">
        <div className="flex items-center justify-center space-x-6">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2 text-purple-500" />
            <span>Quick & Easy</span>
          </div>
          <div className="flex items-center">
            <CheckCircle className="w-4 h-4 mr-2 text-emerald-500" />
            <span>Confidential & Secure</span>
          </div>
        </div>
      </div>
    </div>
  )
}